import {
  ExpandedState,
  ExpandedStateList,
  Updater,
} from "@tanstack/react-table";
import { isFunction } from "lodash";
import { useCallback } from "react";
import useEvent from "react-use-event-hook";
import { useRecoilValue, useSetRecoilState } from "recoil";

import { gridExpandedAF, pageEntityAtom } from "../recoil";

export const useGridExpandedCollapse = () => {
  const entity = useRecoilValue(pageEntityAtom);
  const setGridExpandedCollapse = useSetRecoilState(gridExpandedAF(entity));

  const expandByIds = useEvent((ids: Set<number>): void => {
    setGridExpandedCollapse(prevState => {
      const tmp = { ...prevState };

      return [...ids].reduce((acc, id) => {
        acc[id] = true;
        return acc;
      }, tmp as ExpandedStateList);
    });
  });

  const toggleExpandById = useEvent((id: number): void => {
    setGridExpandedCollapse(prevState => {
      const isExpanded = prevState[id];
      const updatedState = { ...prevState };

      if (isExpanded) {
        delete updatedState[id];
      } else {
        updatedState[id] = true;
      }

      return updatedState;
    });
  });

  const collapseByIds = useEvent((ids: Set<number>): void => {
    setGridExpandedCollapse(prevState => {
      const tmp = { ...prevState };
      Array.from(ids).forEach(id => {
        delete tmp[id];
      });

      return tmp;
    });
  });

  const onExpandedChange = useCallback(
    (updater: Updater<ExpandedState>) => {
      const newExpandedValue = isFunction(updater)
        ? (updater as () => ExpandedState)()
        : updater;
      setGridExpandedCollapse(prevState => {
        const tmp = { ...prevState };
        const ids = Object.keys(newExpandedValue);
        ids.forEach(id => {
          if (id in tmp) {
            delete tmp[id];
          } else {
            tmp[id] = true;
          }
        });

        return tmp;
      });
    },
    [setGridExpandedCollapse],
  );

  const collapseAll = useEvent(() => {
    setGridExpandedCollapse({});
  });

  return {
    toggleExpandById,
    expandByIds,
    collapseByIds,
    onExpandedChange,
    collapseAll,
  };
};
